import type { SeverityLevel, Breadcrumb } from "@sentry/browser";
import type * as Sentry from "@sentry/browser";



export const sentry = {
    sdk: null as typeof Sentry | null,
    runtime: "unknown",
    initCalled: false,
    init: async () => {
        if (sentry.initCalled) {
            console.debug("sentry already initialized");
            return;
        }
        console.debug("sentry initializing");
        sentry.initCalled = true;
        const release = `${process.env.COMMIT_HASH}`

        // @ts-expect-error undefined dpj_runtime. 
        sentry.runtime = dpj_runtime ?? sentry.runtime;
        // @ts-expect-error undefined SENTRY_RELEASE. 
        window.SENTRY_RELEASE = {
            id: release,
        };

        return import("@sentry/browser").then(Sentry => {
            sentry.sdk = Sentry;
            return Sentry.init({
                dsn: "https://61c5e0952eb3384cbfc4800a154dddbf@o4507526370623488.ingest.de.sentry.io/4507526376587344",

                environment: sentry.runtime,
                release: release,

                // https://docs.sentry.io/platforms/javascript/configuration/options/#sendDefaultPii
                sendDefaultPii: true,

                // https://docs.sentry.io/platforms/javascript/configuration/filtering/
                // ignoreErrors: [],
                beforeSend(event) {
                    console.debug(event);
                    event.breadcrumbs = event.breadcrumbs?.filter(breadcrumb => !breadcrumb.data?.url?.includes("clarity.ms"))
                    return event;
                },
                beforeBreadcrumb(breadcrumb) {
                    console.debug(breadcrumb);
                    return breadcrumb;
                },

                // https://docs.sentry.io/platforms/javascript/configuration/integrations/
                integrations: [
                    Sentry.globalHandlersIntegration({
                        onerror: true,
                        onunhandledrejection: true,
                    }),
                    Sentry.browserTracingIntegration(),
                    // Sentry.eventFiltersIntegration({
                    //     allowUrls: [
                    //         /^https:\/\/www\.dpj\.se/,
                    //         /^https:\/\/www\.dpj-workspace\.com/,
                    //     ]
                    // }),
                    Sentry.replayIntegration({
                        maskAllInputs: false,
                        blockAllMedia: false,
                    }),
                    // Sentry.thirdPartyErrorFilterIntegration({
                    // Specify the application keys that you specified in the Sentry bundler plugin
                    //     filterKeys: ["prestashop-theme-dpj"],

                    // Defines how to handle errors that contain third party stack frames.
                    // Possible values are:
                    // - 'drop-error-if-contains-third-party-frames'
                    // - 'drop-error-if-exclusively-contains-third-party-frames'
                    // - 'apply-tag-if-contains-third-party-frames'
                    // - 'apply-tag-if-exclusively-contains-third-party-frames'
                    //     behaviour: "drop-error-if-exclusively-contains-third-party-frames",
                    // }),
                ],
                // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
                tracesSampleRate: .1,
                // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
                tracePropagationTargets: ["localhost", /^https:\/\/www\.dpj\.se/, /^https:\/\/www\.dpj-workspace\.com/],

                // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
                replaysSessionSampleRate: 0.0,
                replaysOnErrorSampleRate: 1.0,
            });
        });
    },

    /** starts a new replay session */
    replayStart: (retry = false) => {
        if (!sentry.initCalled) {
            sentry.init().then(() => {
                sentry.replayStart(true);
            });
            return;
        }

        const rp = sentry.sdk.getReplay();
        if (rp == null) {
            return;
        }
        rp.start();
    },

    /** buffers the current replay session */
    bufferReplay: () => {
        if (!sentry.initCalled) {
            sentry.init().then(() => {
                sentry.bufferReplay();
            });
            return;
        }

        const rp = sentry.sdk.getReplay();
        if (rp == null) {
            return;
        }
        rp.startBuffering();
    },

    /** flushes the current replay session */
    flushReplay: () => {
        if (!sentry.initCalled) {
            sentry.init().then(() => {
                sentry.flushReplay();
            });
            return;
        }
        const rp = sentry.sdk.getReplay();
        if (rp == null) {
            return;
        }
        rp.flush();
    },

    // --- NOTE ---
    // These functions here to allow for lazy loading of the sentry SDK. Might be a better way to do this. I wanted to 
    // hide the import.then syntax from the rest of the codebase.
    // Suggestions welcome.
    // @johan-st
    // --- 

    /**
     * @returns string "pending" if sentry is not initialized, otherwise the result of sentry.sdk.captureException(error)
     * 
     * @note This is a wrapper around sentry.sdk.captureException(error) that allows for lazy loading of the sentry SDK. 
     *       If sentry is not initialized, it will load and call the sdk inside a promise and immediately return undefined.
     */
    captureException: (error: Error) => {
        if (sentry.sdk == null) {
            import("@sentry/browser").then(Sentry => {
                Sentry.captureException(error);
            });
            return "pending";
        }
        return sentry.sdk.captureException(error);
    },
    /**
     * @returns string "pending" if sentry is not initialized, otherwise the result of sentry.sdk.captureEvent(event)
     * 
     * @note This is a wrapper around sentry.sdk.captureEvent(event) that allows for lazy loading of the sentry SDK. 
     *       If sentry is not initialized, it will load and call the sdk inside a promise and immediately return undefined.
     */
    captureEvent: (event: Sentry.Event) => {
        if (sentry.sdk == null) {
            import("@sentry/browser").then(Sentry => {
                Sentry.captureEvent(event);
            });
            return "pending";
        }
        return sentry.sdk.captureEvent(event);
    },
    /**
     * @returns string "pending" if sentry is not initialized, otherwise the result of sentry.sdk.captureMessage(message, level)
     * 
     * @note This is a wrapper around sentry.sdk.captureMessage(message, level) that allows for lazy loading of the sentry SDK. 
     *       If sentry is not initialized, it will load and call the sdk inside a promise and immediately return undefined.
     */
    captureMessage: (message: string, level?: SeverityLevel) => {
        if (sentry.sdk == null) {
            import("@sentry/browser").then(Sentry => {
                Sentry.captureMessage(message, level);
            });
            return "pending";
        }
        return sentry.sdk.captureMessage(message, level);
    },

    /**
     * @returns string "pending" if sentry is not initialized, otherwise the result of sentry.sdk.addBreadcrumb(breadcrumb)
     * 
     * @note This is a wrapper around sentry.sdk.addBreadcrumb(breadcrumb) that allows for lazy loading of the sentry SDK. 
     *       If sentry is not initialized, it will load and call the sdk inside a promise and immediately return undefined.
     */
    breadcrumb: (breadcrumb: Breadcrumb) => {
        if (sentry.sdk == null) {
            import("@sentry/browser").then(Sentry => {
                Sentry.addBreadcrumb(breadcrumb);
            });
            return "pending";
        }
        return sentry.sdk.addBreadcrumb(breadcrumb);
    }
}

